"use client";

/**
 * `useVisibleAnimation` — returns `[ref, active]` for elements that
 * should ONLY animate when on-screen AND the tab is visible.
 *
 * Why: animations that run while the user can't see them waste CPU
 * (Lighthouse "minimize main-thread work") and battery (mobile). The
 * hook combines:
 *   - `IntersectionObserver` — true once any pixel of the element is
 *     in the viewport, false when it leaves
 *   - `document.visibilityState` — true while the tab is foregrounded
 *
 * Pass the returned `ref` to the animated element and gate your
 * `setInterval` / state machine on the `active` boolean.
 *
 *   const [ref, active] = useVisibleAnimation<HTMLDivElement>();
 *   useEffect(() => {
 *     if (!active) return;
 *     const id = setInterval(tick, 700);
 *     return () => clearInterval(id);
 *   }, [active]);
 *   return <div ref={ref}>...</div>;
 *
 * The hook also respects `prefers-reduced-motion`: when the user has
 * the OS preference enabled, `active` stays `false` so animations
 * never start. Components should render their final state in that
 * case.
 */

import { useEffect, useRef, useState } from "react";

export function useVisibleAnimation<T extends Element>(): [
  React.RefObject<T>,
  boolean,
] {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setInView(entry.isIntersecting);
      },
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    onVis();
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const active = !reducedMotion && inView && tabVisible;
  return [ref as React.RefObject<T>, active];
}
